import { MetaField } from '@rossum/api-client/shared';
import { OperatorEncoder } from './types';

const simple =
  (mqlOperator: string): OperatorEncoder =>
  (field, value) => ({ [field]: { [mqlOperator]: value } });

export const dataGridOperatorsMap = {
  isEmpty: field => ({ [field]: { $emptyOrMissing: true } }),
  isNotEmpty: field => ({ [field]: { $emptyOrMissing: false } }),
  contains: simple('$containsPrefixes'),
  equals: simple('$eq'),
  startsWith: simple('$startsWith'),
  isAnyOf: simple('$in'),
  notIn: simple('$nin'),
  is: simple('$eq'),
  not: simple('$ne'),
  after: simple('$gt'),
  onOrAfter: simple('$gte'),
  before: simple('$lt'),
  onOrBefore: simple('$lte'),
  '=': simple('$eq'),
  '!=': simple('$ne'),
  '>': simple('$gt'),
  '>=': simple('$gte'),
  '<': simple('$lt'),
  '<=': simple('$lte'),
} satisfies Record<string, OperatorEncoder>;

export const mongoDbOperatorsMap = {
  isEmpty: field => ({ [field]: { $exists: false } }),
  isNotEmpty: field => ({ [field]: { $exists: true } }),
  contains: (field, value) => ({ [field]: { $regex: value, $options: 'i' } }),
  startsWith: (field, value) => ({
    [field]: { $regex: `^${value?.toString()}`, $options: 'i' },
  }),
  equals: simple('$eq'),
  isAnyOf: simple('$in'),
  notIn: simple('$nin'),
  is: simple('$eq'),
  not: simple('$ne'),
  after: simple('$gt'),
  onOrAfter: simple('$gte'),
  before: simple('$lt'),
  onOrBefore: simple('$lte'),
  '=': simple('$eq'),
  '!=': simple('$ne'),
  '>': simple('$gt'),
  '>=': simple('$gte'),
  '<': simple('$lt'),
  '<=': simple('$lte'),
} satisfies Record<string, OperatorEncoder>;

export const detailsColumnOptionKeys = {
  automated: 'automated',
  automatically_rejected: 'automatically_rejected',
  emails: 'emails',
  attachments: 'attachments',
  duplicates: 'duplicates',
  edit: 'edit',
  einvoice: 'einvoice',
  has_note: 'has_note',
  has_suggested_edit: 'has_suggested_edit',
  excluded_from_training: 'excluded_from_training',
};

export const CODEC_FIELD_TYPES = ['bool', 'date', 'number', 'string'] as const;
/**
 * Configure fields that have specific value processing (e.g. value transformation for boolean, number) or
 * operator resolvence (e.g. $eq can mean 'is' for date but '=' for number) based on their type.
 */
export type CodecFieldType = (typeof CODEC_FIELD_TYPES)[number];
export type CodecFieldTypes = Record<string, CodecFieldType>;

// CodecFieldTypes are defined for those fields that subject to mql transformation (e.g. document list filtering)
export const metaColumnsCodecTypesMap = {
  annotation: 'string',
  assignees: null,
  queue: null,
  status: null,
  assigned_at: 'date',
  original_file_name: 'string',
  labels: null,
  details: null,
  relations: null,
  created_at: 'date',
  modified_at: 'date',
  exported_at: 'date',
  confirmed_at: 'date',
  rejected_at: 'date',
  deleted_at: 'date',
  modifier: null,
  exported_by: null,
  confirmed_by: null,
  rejected_by: null,
  deleted_by: null,
  export_failed_at: 'date',
  creator: null,
  training_enabled: null,
  workflow_step: null,
} as const satisfies Record<MetaField, CodecFieldType | null>;
